import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '../lib/utils';

const alertVariants = cva(
  // Padding + icon inset track --space-base (the density knob button.tsx
  // sizes ride) and the font keeps a rem floor — at `touch` density the
  // calc() branch wins so alert copy scales up with the surrounding
  // controls instead of rendering at laptop size on a kiosk.
  'relative w-full rounded-lg border px-[calc(var(--space-base)*3)] py-[calc(var(--space-base)*2.5)] text-[length:max(0.875rem,calc(var(--space-base)*2.8))] [&>svg+div]:translate-y-[-3px] [&>svg]:absolute [&>svg]:left-[calc(var(--space-base)*3)] [&>svg]:top-[calc(var(--space-base)*2.5)] [&>svg]:text-foreground [&>svg~*]:pl-7',
  {
    variants: {
      variant: {
        default: 'bg-background text-foreground',
        destructive:
          'border-destructive/50 text-destructive dark:border-destructive [&>svg]:text-destructive',
        warning:
          'border-[hsl(var(--alarm-minor))]/50 text-[hsl(var(--alarm-minor))] [&>svg]:text-[hsl(var(--alarm-minor))]',
        info: 'border-[hsl(var(--alarm-advisory))]/50 text-[hsl(var(--alarm-advisory))] [&>svg]:text-[hsl(var(--alarm-advisory))]',
        success:
          'border-[hsl(var(--state-running))]/50 text-[hsl(var(--state-running))] [&>svg]:text-[hsl(var(--state-running))]',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  }
);

const Alert = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & VariantProps<typeof alertVariants>
>(({ className, variant, role, ...props }, ref) => {
  // WCAG 4.1.3 — status messages. `destructive`/`warning` carry
  // `role="alert"` (assertive, interrupts the screen reader). `info` /
  // `success` use `role="status"` (polite, queued behind the current
  // utterance) so the operator doesn't get over-paged on every
  // green-path notification.
  const resolvedRole =
    role ??
    (variant === 'destructive' || variant === 'warning' ? 'alert' : 'status');
  const ariaLive =
    props['aria-live'] ??
    (resolvedRole === 'alert' ? 'assertive' : 'polite');
  return (
    <div
      ref={ref}
      role={resolvedRole}
      aria-live={ariaLive}
      className={cn(alertVariants({ variant }), className)}
      {...props}
    />
  );
});
Alert.displayName = 'Alert';

const AlertTitle = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLHeadingElement>
>(({ className, children, ...props }, ref) => (
  <h5
    ref={ref}
    className={cn('mb-1 font-medium leading-none tracking-tight', className)}
    {...props}
  >
    {children}
  </h5>
));
AlertTitle.displayName = 'AlertTitle';

const AlertDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('text-[length:inherit] [&_p]:leading-relaxed', className)}
    {...props}
  />
));
AlertDescription.displayName = 'AlertDescription';

export { Alert, AlertTitle, AlertDescription, alertVariants };
